import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Pagination } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, CheckCircle, XCircle, Key, Plus, Edit, Ban, Check } from "lucide-react";
import { formatDateIST } from "@/utils/dateUtils";

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-800",
  admin_user: "bg-indigo-100 text-indigo-800",
  seller_admin: "bg-blue-100 text-blue-800",
  seller_user: "bg-cyan-100 text-cyan-800",
  pharmeasy_hub_operator: "bg-emerald-100 text-emerald-800",
  pharmeasy_rider: "bg-green-100 text-green-800",
  hub_manager: "bg-teal-100 text-teal-800",
  account_manager: "bg-amber-100 text-amber-800"
};

const ROLE_LABELS = {
  admin: "Admin",
  admin_user: "Admin User",
  seller_admin: "Seller Admin",
  seller_user: "Seller User",
  pharmeasy_hub_operator: "Pharmeasy Hub Operator",
  pharmeasy_rider: "Pharmeasy Rider",
  hub_manager: "Hub Manager",
  account_manager: "Account Manager"
};

// Sentinel for the custom-role select ("no custom role" -> role_id null on assign)
const SYSTEM_DEFAULT_ROLE = "system_default";
// Principal types that can carry a custom platform role
const CUSTOM_ROLE_TARGETS = ['admin_user', 'account_manager'];

const UsersManagement = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [pharmeasyHubs, setPharmeasyHubs] = useState([]);
  const [platformRoles, setPlatformRoles] = useState([]);
  const [customRoleId, setCustomRoleId] = useState(SYSTEM_DEFAULT_ROLE);
  const [loading, setLoading] = useState(true);

  // Client-side filters + pagination (lists are small; filtered array is sliced locally)
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Password Reset Dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Add/Edit User Dialog
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'seller_user',
    seller_id: '',
    assigned_hub_id: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchSellers();
    fetchPharmeasyHubs();
    // Role management (and assignment) is hard-locked to the admin superuser
    if (user?.role === 'admin') {
      fetchPlatformRoles();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      console.error("Failed to fetch sellers");
    }
  };

  const fetchPharmeasyHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs/`);
      setPharmeasyHubs(response.data || []);
    } catch (error) {
      console.error("Failed to fetch Pharmeasy hubs:", error);
    }
  };

  const fetchPlatformRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      const list = Array.isArray(response.data) ? response.data : response.data?.roles || [];
      setPlatformRoles(list.filter((r) => !r.is_system));
    } catch (error) {
      console.error("Failed to fetch platform roles:", error);
    }
  };

  const handleAssignCustomRole = async (value) => {
    const previous = customRoleId;
    setCustomRoleId(value);
    try {
      await axios.post(`${API}/roles/assign`, {
        user_id: editingUser.id,
        role_id: value === SYSTEM_DEFAULT_ROLE ? null : value
      });
      toast.success(value === SYSTEM_DEFAULT_ROLE
        ? "Custom role cleared - system default permissions apply"
        : "Custom role assigned");
      fetchUsers();
    } catch (error) {
      setCustomRoleId(previous);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update custom role");
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/admin/reset-user-password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${selectedUser.name}`);
      setShowPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = (u) => {
    setSelectedUser(u);
    setShowPasswordDialog(true);
  };

  const openUserDialog = (u = null) => {
    setCustomRoleId(u?.role_id || SYSTEM_DEFAULT_ROLE);
    if (u) {
      setEditingUser(u);
      setFormData({
        name: u.name,
        email: u.email,
        phone: u.phone || '',
        password: '',
        role: u.role,
        seller_id: u.seller_id || '',
        assigned_hub_id: u.assigned_hub_id || '',
        is_active: u.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'seller_user',
        seller_id: '',
        assigned_hub_id: '',
        is_active: true
      });
    }
    setShowUserDialog(true);
  };

  const closeUserDialog = () => {
    setShowUserDialog(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'seller_user',
      seller_id: '',
      assigned_hub_id: '',
      is_active: true
    });
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error("Password is required for new users");
      return;
    }

    if ((formData.role === 'seller_admin' || formData.role === 'seller_user') && !formData.seller_id) {
      toast.error("Please select a seller for seller users");
      return;
    }

    if (formData.role === 'pharmeasy_hub_operator' && !formData.assigned_hub_id) {
      toast.error("Please select a Pharmeasy Hub for hub operators");
      return;
    }

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        is_active: formData.is_active
      };

      // Only add seller_id for seller roles (not for admin or admin_user)
      if (formData.role === 'seller_admin' || formData.role === 'seller_user') {
        userData.seller_id = formData.seller_id;
      }

      // Add assigned_hub_id for hub operators
      if (formData.role === 'pharmeasy_hub_operator') {
        userData.assigned_hub_id = formData.assigned_hub_id;
      }

      // Add password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, userData);
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/admin/users`, userData);
        toast.success("User created successfully");
      }

      closeUserDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const newStatus = !user.is_active;
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_active: newStatus
      });
      toast.success(`User ${newStatus ? 'enabled' : 'disabled'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  // Distinct roles present in the data, for the Role filter dropdown
  const roleOptions = useMemo(
    () => [...new Set(users.map((u) => u.role).filter(Boolean))].sort(),
    [users]
  );

  // Search + Role + Status combined, applied client-side
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter === 'active' && !u.is_active) return false;
      if (statusFilter === 'inactive' && u.is_active) return false;
      if (q) {
        const haystack = `${u.name || ''} ${u.email || ''} ${u.phone || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = useMemo(
    () => filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredUsers, page]
  );

  // Reset to page 1 whenever a filter/search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter, statusFilter]);

  // Keep the page in range if the filtered set shrinks (e.g. after a status toggle)
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const requiresSeller = formData.role === 'seller_admin' || formData.role === 'seller_user';
  const requiresHub = formData.role === 'pharmeasy_hub_operator';

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Users</h1>
            <p className="text-slate-600">Manage all system users</p>
          </div>
          <Button
            style={{ backgroundColor: '#00A699' }}
            onClick={() => openUserDialog()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Search + Filters */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <SearchInput onSearch={handleSearch} placeholder="Search users by email or name..." />
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-52">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roleOptions.map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No users found</p>
                <Button onClick={() => openUserDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First User
                </Button>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">No users match the current filters</p>
              </div>
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Seller/Hub</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {pagedUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-900">{u.name}</span>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <span className="text-sm text-slate-600">{u.email}</span>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <Badge className={ROLE_COLORS[u.role]}>
                            {ROLE_LABELS[u.role]}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          {u.seller_info?.brand_name ? (
                            <div>
                              <span className="text-sm font-medium text-slate-700">{u.seller_info.brand_name}</span>
                              <br />
                              <span className="text-xs text-slate-500">{u.seller_info.company_registered_name}</span>
                            </div>
                          ) : u.hub_info?.hub_name ? (
                            <div>
                              <span className="text-sm font-medium text-emerald-700">{u.hub_info.hub_name}</span>
                              <br />
                              <span className="text-xs text-slate-500">{u.hub_info.hub_code}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <span className="text-sm text-slate-600">{u.phone || '-'}</span>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <div className="flex items-center">
                            {u.is_active ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                                <span className="text-sm">Active</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 text-red-500 mr-1" />
                                <span className="text-sm">Inactive</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <span className="text-sm text-slate-600">{formatDateIST(u.created_at)}</span>
                        </td>
                        <td className="px-4 py-4 align-middle whitespace-nowrap">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openUserDialog(u)}
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openResetDialog(u)}
                              title="Reset password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={u.is_active ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                              onClick={() => handleToggleUserStatus(u)}
                              title={u.is_active ? "Disable user" : "Enable user"}
                            >
                              {u.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-slate-200">
                <Pagination
                  page={page}
                  pages={totalPages}
                  total={filteredUsers.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information' : 'Create a new user for the system'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitUser} className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
                required
                disabled={!!editingUser}
                className={`mt-1 ${editingUser ? 'bg-slate-100 cursor-not-allowed' : ''}`}
              />
              {editingUser && (
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed after user creation</p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1234567890"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">
                Password {!editingUser && '*'}
                {editingUser && <span className="text-xs text-slate-500"> (Leave blank to keep current)</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                required={!editingUser}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="role">Role *</Label>
              {/* Only admin role can edit role, admin_user cannot */}
              {editingUser && user?.role !== 'admin' ? (
                <>
                  <Input
                    value={ROLE_LABELS[formData.role] || formData.role}
                    disabled
                    className="mt-1 bg-slate-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-500 mt-1">Only Admin can change user roles</p>
                </>
              ) : (
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({...formData, role: value, seller_id: ''})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="admin_user">Admin User</SelectItem>
                    <SelectItem value="account_manager">Account Manager</SelectItem>
                    <SelectItem value="seller_admin">Seller Admin</SelectItem>
                    <SelectItem value="seller_user">Seller User</SelectItem>
                    <SelectItem value="pharmeasy_hub_operator">Pharmeasy Hub Operator</SelectItem>
                    <SelectItem value="pharmeasy_rider">Pharmeasy Rider</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {user?.role === 'admin' && editingUser && CUSTOM_ROLE_TARGETS.includes(formData.role) && (
              <div>
                <Label htmlFor="custom-role">Custom Role</Label>
                <Select value={customRoleId} onValueChange={handleAssignCustomRole}>
                  <SelectTrigger id="custom-role" className="mt-1" data-testid="custom-role-select">
                    <SelectValue placeholder="System default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SYSTEM_DEFAULT_ROLE} data-testid="custom-role-option-default">
                      System default
                    </SelectItem>
                    {platformRoles.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`custom-role-option-${r.name}`}>
                        {r.display_name || r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Applies immediately. A custom role replaces the system default permissions for this user
                  — manage roles under Roles &amp; Permissions.
                </p>
              </div>
            )}

            {requiresSeller && (
              <div>
                <Label htmlFor="seller_id">Select Seller/Brand *</Label>
                <Select value={formData.seller_id} onValueChange={(value) => setFormData({...formData, seller_id: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose a seller..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sellers.map((seller) => (
                      <SelectItem key={seller.seller_id} value={seller.seller_id}>
                        {seller.brand_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.seller_id && (
                  <p className="text-xs text-slate-500 mt-1">
                    This user will have access to {sellers.find(s => s.seller_id === formData.seller_id)?.brand_name || 'the selected seller'}'s orders and data
                  </p>
                )}
              </div>
            )}

            {requiresHub && (
              <div>
                <Label htmlFor="assigned_hub_id">Select Pharmeasy Hub *</Label>
                <Select value={formData.assigned_hub_id} onValueChange={(value) => setFormData({...formData, assigned_hub_id: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose a hub..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pharmeasyHubs.map((hub) => (
                      <SelectItem key={hub._id} value={hub._id}>
                        {hub.hub_name}{hub.hub_code ? ` (${hub.hub_code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.assigned_hub_id && (
                  <p className="text-xs text-slate-500 mt-1">
                    This operator will only see inward sessions for this hub
                  </p>
                )}
              </div>
            )}

            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="isActive" className="font-normal cursor-pointer">
                  Active User
                </Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeUserDialog}>
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: '#00A699' }}>
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter a new password for <strong>{selectedUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resetting}>
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AdminLayout>
  );
};

export default UsersManagement;
